/**
 * DataProfile.tsx
 * Bar chart showing data-value distribution for a field.
 * Redesigned — uses shared GenWizard light theme.
 */

import React, { Component } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title     as ChartTitle,
  Tooltip   as ChartTooltip,
  Legend    as ChartLegend,
} from 'chart.js';
import type { ChartData, TooltipItem, TooltipModel } from 'chart.js';

import { T, BAR_PALETTE, injectTheme } from './theme/theme';
import { multiSort } from './constants';

Chart.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, ChartLegend, ChartDataLabels);

// ─── Types ────────────────────────────────────────────────────────────────────
interface DataProfileProps {
  tableData:  number;
  columnData: any;
}

interface ChartDataset {
  label:           string;
  data:            number[];
  backgroundColor: string[];
  borderRadius:    number;
  borderSkipped:   false;
}

interface DataProfileState {
  chartData: { labels: string[]; datasets: ChartDataset[] };
}

// ─── Component ────────────────────────────────────────────────────────────────
class DataProfile extends Component<DataProfileProps, DataProfileState> {
  constructor(props: DataProfileProps) {
    super(props);
    this.state = { chartData: this.buildChartData(props.columnData) };
  }

  componentDidMount() {
    injectTheme();
  }

  componentDidUpdate(prev: DataProfileProps) {
    if (prev.columnData !== this.props.columnData) {
      this.setState({ chartData: this.buildChartData(this.props.columnData) });
    }
  }

  // ── Data builder ──────────────────────────────────────────────────────────
  buildChartData(columnData: any): DataProfileState['chartData'] {
    const sorted  = multiSort(columnData || [], [{ key: 'CNT', order: 'desc' }]);
    const colors  = sorted.map((_: any, i: number) => BAR_PALETTE[i % BAR_PALETTE.length] as string);
    return {
      labels: sorted.map((m: any) => String(m.DATA ?? '<NULL>')),
      datasets: [{
        label:           'Records',
        data:            sorted.map((m: any) => m.CNT || 0),
        backgroundColor: colors,
        borderRadius:    4,
        borderSkipped:   false,
      }],
    };
  }

  // ── Chart options ─────────────────────────────────────────────────────────
  buildOptions() {
    const tableData = this.props.tableData;

    const tooltipLabelCb = function (
      this: TooltipModel<'bar'>,
      item: TooltipItem<'bar'>,
    ): string {
      const data    = item.chart.data as ChartData<'bar'>;
      const arr     = data.datasets[item.datasetIndex].data as number[];
      const total   = arr.reduce((a, v) => a + (v as number), 0);
      const current = arr[item.dataIndex] as number;
      const pct     = total > 0 ? ((current / total) * 100).toFixed(1) : '0.0';
      return `${current.toLocaleString()} (${pct}%)`;
    };

    return {
      responsive:          true,
      maintainAspectRatio: true,
      scales: {
        x: {
          grid:  { display: false },
          ticks: { font: { size: 11 }, color: T.textMuted, maxRotation: 35 },
        },
        y: {
          beginAtZero: true,
          max:         tableData,
          grid:        { color: T.border },
          ticks:       { font: { size: 11 }, color: T.textMuted },
          title:       { display: true, text: 'Records', font: { size: 11 }, color: T.textMuted },
        },
      },
      plugins: {
        legend:  { display: false },
        title:   { display: false },
        tooltip: {
          callbacks:   { label: tooltipLabelCb },
          backgroundColor: T.bgCard,
          titleColor:  T.textPrimary,
          bodyColor:   T.textMuted,
          borderColor: T.border,
          borderWidth: 1,
        },
        datalabels: {
          anchor:    'end'  as const,
          align:     'top'  as const,
          formatter: (_: any, ctx: any) => {
            const d     = ctx.dataset.data as number[];
            const total = d.reduce((a: number, v: number) => a + v, 0);
            const pct   = total > 0 ? ((d[ctx.dataIndex] / total) * 100).toFixed(0) : '0';
            return +pct >= 5 ? `${pct}%` : '';
          },
          font:  { size: 10, weight: 'bold' as const },
          color: T.textMuted,
        },
      },
    };
  }

  // ── Render ────────────────────────────────────────────────────────────────
  render() {
    const { chartData } = this.state;
    const distinctCount = chartData.labels.length;

    return (
      <div className="gw-chart-card">
        <div className="gw-chart-hdr">
          <div className="gw-chart-title">Data Distribution</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textMuted }}>
              {distinctCount} distinct value{distinctCount !== 1 ? 's' : ''}
            </span>
            {this.props.tableData > 0 && <>
              <span style={{ fontSize: 11, color: T.textMuted }}>·</span>
              <span style={{ fontSize: 11, color: T.textMuted }}>
                {this.props.tableData.toLocaleString()} total records
              </span>
            </>}
          </div>
        </div>
        <div className="gw-chart-body">
          <Bar
            height={90}
            data={chartData}
            options={this.buildOptions()}
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>
    );
  }
}

export default DataProfile;